import { combineLatest, takeUntil } from 'rxjs';

import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AuthService } from '@auth/services/auth/auth.service';
import { AdjustStockModalComponent } from '@inventory/components/adjust-stock-modal/adjust-stock-modal.component';
import {
	IInventoryItem,
	IInventoryLocation,
	IInventoryLocationItemWithData,
	IInventoryStockAdjustmentWithData,
} from '@inventory/models/inventory';
import { IInventoryItemLocationCreateForm } from '@inventory/models/inventory-forms';
import { InventoryItemsLocationsService } from '@inventory/services/inventory-items-locations/inventory-items-locations.service';
import { InventoryLocationsService } from '@inventory/services/inventory-locations/inventory-locations.service';
import { InventoryStockAdjustmentService } from '@inventory/services/inventory-stock-adjustment/inventory-stock-adjustment.service';
import { AlertComponent } from '@shared/components/alerts/alert/alert.component';
import { DropdownControlComponent, IDropdownOption } from '@shared/components/forms/dropdown-control/dropdown-control.component';
import { StandardControlComponent } from '@shared/components/forms/standard-control/standard-control.component';
import { LoadingStateComponent } from '@shared/components/loading-state/loading-state.component';
import { AlertTypes } from '@shared/models/alert';
import { ToastTypes } from '@shared/models/toast';
import { NullDefaultPipe } from '@shared/pipes/null-default.pipe';
import { ModalService } from '@shared/services/modal/modal.service';
import { OffcanvasService } from '@shared/services/offcanvas/offcanvas.service';
import { ToastService } from '@shared/services/toast/toast.service';

@Component({
	selector: 'app-inventory-item-stock-management-offcanvas',
	imports: [CommonModule, StandardControlComponent, DropdownControlComponent, LoadingStateComponent, NullDefaultPipe, AlertComponent],
	templateUrl: './inventory-item-stock-management-offcanvas.component.html',
	styleUrl: './inventory-item-stock-management-offcanvas.component.scss',
	host: {
		class: 'h-100',
		style: 'display: contents',
	},
})
export class InventoryItemStockManagementOffcanvasComponent implements OnInit, OnDestroy {
	@Input() inventoryItem: IInventoryItem;

	private inventoryLocationsService = inject(InventoryLocationsService);
	private inventoryItemsLocationsService = inject(InventoryItemsLocationsService);
	private inventoryStockAdjustmentService = inject(InventoryStockAdjustmentService);

	private toastService = inject(ToastService);
	private offcanvasService = inject(OffcanvasService);
	private modalService = inject(ModalService);
	private authService = inject(AuthService);

	inventoryItemLocations: Array<IInventoryLocationItemWithData> = [];
	inventoryLocations: Array<IInventoryLocation> = [];
	inventoryStockAdjustments: Array<IInventoryStockAdjustmentWithData> = [];
	inventoryLocationOptions: Array<IDropdownOption> = [];

	destroyed$: EventEmitter<void> = new EventEmitter<void>();
	isLoading = true;
	isAddingToLocation = false;
	AlertTypes = AlertTypes;

	form: FormGroup<IInventoryItemLocationCreateForm>;

	ngOnInit(): void {
		this.form = new FormGroup<IInventoryItemLocationCreateForm>({
			location: new FormControl<string | null>(null, [Validators.required]),
			quantity: new FormControl<number | null>(null, [Validators.required, Validators.min(0)]),
		});

		combineLatest([
			this.inventoryLocationsService.getInventoryLocationsByOrganisation(this.inventoryItem.organisation, false),
			this.inventoryStockAdjustmentService.getInventoryStockAdjustmentsByItemWithData(this.inventoryItem.id!),
			this.inventoryItemsLocationsService.getInventoryItemLocationsWithDataForItem(
				this.inventoryItem.organisation,
				this.inventoryItem.id!,
			),
		])
			.pipe(takeUntil(this.destroyed$))
			.subscribe(([locations, adjustments, itemLocations]) => {
				this.inventoryLocations = locations;
				this.inventoryStockAdjustments = adjustments;
				this.inventoryItemLocations = itemLocations;
				this.createInventoryLocationDropdownOptions();
				this.isLoading = false;

				if (this.hasPendingAdjustments) {
					this.form.disable();
				} else {
					this.form.enable();
				}
			});
	}

	close(): void {
		this.offcanvasService.dismissLast();
	}

	ngOnDestroy(): void {
		this.destroyed$.emit();
	}

	get hasPendingAdjustments(): boolean {
		return this.inventoryStockAdjustments.some((adj) => adj.status === 'pending');
	}

	createInventoryLocationDropdownOptions(): void {
		const usedLocationIds = new Set(this.inventoryItemLocations.map((itemLoc) => itemLoc.location.id));
		this.inventoryLocationOptions = this.inventoryLocations
			.filter((loc) => !usedLocationIds.has(loc.id!))
			.map((loc) => ({
				id: loc.id!,
				value: loc.id!,
				label: loc.name,
				icon: 'bi-geo-alt-fill',
			}));
	}

	addToLocation() {
		if (this.form.invalid) {
			this.form.markAllAsTouched();
			return;
		}

		this.isAddingToLocation = true;

		this.inventoryStockAdjustmentService
			.createInventoryStockAdjustmentById(
				this.inventoryItem.organisation,
				this.authService.currentUser?.uid,
				this.inventoryItem.id!,
				this.form.controls.location.value as string,
				this.form.controls.quantity.value as number,
				'increase',
				'Initial quantity set',
			)
			.then(() => {
				this.isAddingToLocation = false;
				this.form.reset();
				this.createInventoryLocationDropdownOptions();
				this.toastService.showToast({
					title: 'Inventory Item',
					message: `Stock added to location successfully.`,
					type: ToastTypes.SUCCESS,
				});
			})
			.catch((error) => {
				console.error('Error adding stock to location', error);
				this.isAddingToLocation = false;
				this.toastService.showToast({
					title: 'Inventory Item',
					message: `Failed to add stock to location: ${error.message}`,
					type: ToastTypes.ERROR,
				});
			});
	}

	adjustStockQuantity(inventoryItemLocation: IInventoryLocationItemWithData): void {
		const modalRef = this.modalService.open(AdjustStockModalComponent, false, 'md');
		const componentInstance = modalRef.componentInstance as AdjustStockModalComponent;
		componentInstance.inventoryItemLocation = inventoryItemLocation;
		componentInstance.organisationRef = this.inventoryItem.organisation;
	}
}
